import * as React from "react";
import { NavItem, NavLink } from "reactstrap";
import IconTextButton from "../../iconTextButton/IconTextButton";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { ITableAction, ITableActionName } from "../../../types/ITableAction";
import { ITabReduxItem } from "../../../../redux/types/ITabReduxItem";
import { ManualMergeOptions } from "../../../types/ManualMergeOptions";
import { ADD_COVER_VERSION_ACTION } from "../../../../membersportal/Consts";
import {
    ADD_NEW_ACTION,
    ADD_NEW_DATE_ACTION,
    CODE_ACTION,
    CONTRIBUTORS_KEY,
    DEPENDENT_ROW_ACTION,
    DISTRIBUTION_POOLS_KEY,
    GROUPED_ACTION,
    MERGE_ACTION,
    OPEN_ACTION,
    REMOVE_ACTION,
    SEARCH_ACTION,
    WORK_RESPONSE,
    WORK_SUBMISSION,
} from "../../../Consts";
import { ArrowMaximize20Regular, Delete20Regular, Open20Regular } from "@fluentui/react-icons";

interface IRenderGroupedActionsParams {
    tableContentItem: any;
    field: IRepertoireField;
    groupActionClick: (action: any, tableContentItem: any) => void;
}

export interface IRenderRowActionParams {
    tableContentItem: any;
    field: IRepertoireField;
    stateKey: string;
    componentInstance: string;
    isReadOnly?: boolean;
    tabs?: ITabReduxItem[];
    activeTab?: number;
    tableActions?: ITableAction[];
    onClickOpen: (field: IRepertoireField, tableContentItem: any) => void;
    onClickAdd: () => void;
    onClickRemove: (tableContentItem: any) => void;
    showSearchModal: (modalProps: any, searchViewKey: string, index?: number) => void;
    dependentRowAction: (tableContentItem: any, event?: React.MouseEvent) => void;
    onOpenCodeModel: (submission: string, type: string) => void;
    renderGroupedActions: (params: IRenderGroupedActionsParams) => JSX.Element;
    mergeActionClick?: (tableContentItem: any) => void;
    groupActionClick: (action: any, tableContentItem: any) => void;
}

export const renderGroupedActions = ({
    tableContentItem,
    field,
    groupActionClick,
}: IRenderGroupedActionsParams): JSX.Element => {
    const actions = JSON.parse(field.additionalData);
    const buttons: JSX.Element[] = [];
    const dropdowns: JSX.Element[] = [];
    let index = 0;

    let disableAll = false;

    if (tableContentItem.isDefinite.value === true) {
        if (actions.hasOwnProperty(ManualMergeOptions.PostNew)) {
            delete actions[ManualMergeOptions.PostNew];
        }
    }

    if (tableContentItem?.name?.value === "" && tableContentItem?.number?.value === "") {
        disableAll = true;
    }

    if (actions) {
        for (const actionKey in actions) {
            if (index === 0) {
                buttons.push(
                    <button
                        disabled={disableAll}
                        type="button"
                        key={index}
                        title={actions[actionKey]}
                        onClick={() => groupActionClick(actionKey, tableContentItem)}
                        className="button btn-defaultSecondary"
                    >
                        <div className="overflowEllipsis widthJoinedButton flexRow">
                            {actions[actionKey]}
                        </div>
                    </button>
                );

                buttons.push(
                    <button
                        disabled={disableAll}
                        title="More options"
                        key={-1}
                        type="button"
                        className="button btn-defaultSecondary dropdown-toggle"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        <span className="caret"></span>
                        <span className="sr-only">Toggle Dropdown</span>
                    </button>
                );
            } else {
                dropdowns.push(
                    <NavItem key={index}>
                        <NavLink
                            disabled={disableAll}
                            onClick={() => groupActionClick(actionKey, tableContentItem)}
                        >
                            {actions[actionKey]}
                        </NavLink>
                    </NavItem>
                );
            }

            index += 1;
        }
    }

    buttons.push(
        <ul className="dropdown-menu" key={-2}>
            {dropdowns}
        </ul>
    );

    return (
        <div className="btn-group splitButton minWidthMaxContent">
            {buttons}
        </div>
    );
};

export const renderRowAction = ({
    tableContentItem,
    field,
    stateKey,
    componentInstance,
    isReadOnly,
    tabs = [],
    activeTab = 0,
    tableActions = [],
    onClickOpen,
    onClickAdd,
    onClickRemove,
    showSearchModal,
    dependentRowAction,
    onOpenCodeModel,
    renderGroupedActions,
    mergeActionClick,
    groupActionClick,
}: IRenderRowActionParams): JSX.Element | null => {
    const tableActionName: ITableActionName = field.fieldType as ITableActionName;
    const tableAction = tableActions.filter(action => action.name === tableActionName);
    const isDisabledSpecified = tableAction[0] ? tableAction[0].disabled !== undefined : false;

    switch (tableActionName) {
        case OPEN_ACTION: {
            return (
                <td className="td" key={OPEN_ACTION}>
                    <div className="tableCell">
                        <IconTextButton
                            icon={Open20Regular}
                            text={""}
                            onClick={() => onClickOpen(field, tableContentItem)}
                            disabled={isDisabledSpecified ? tableAction[0].disabled : isReadOnly}
                        />
                    </div>
                </td>
            );
        }

        case ADD_NEW_ACTION: {
            return (
                <td className="td" key={ADD_NEW_ACTION}>
                    <div className="tableCell">
                        <IconTextButton
                            icon={Delete20Regular}
                            text={field.data}
                            onClick={onClickAdd}
                            disabled={isReadOnly}
                        />
                    </div>
                </td>
            );
        }

        case ADD_COVER_VERSION_ACTION: {
            return (
                <td className="td" key={ADD_COVER_VERSION_ACTION}>
                    <div className="tableCell">
                        <IconTextButton
                            icon={Delete20Regular}
                            text={field.data}
                            onClick={onClickAdd}
                            disabled={isReadOnly}
                        />
                    </div>
                </td>
            );
        }

        case ADD_NEW_DATE_ACTION: {
            return (
                <td className="td" key={ADD_NEW_DATE_ACTION}>
                    <div className="tableCell">
                        <IconTextButton
                            icon={Delete20Regular}
                            text={field.data}
                            onClick={onClickAdd}
                            disabled={isReadOnly}
                        />
                    </div>
                </td>
            );
        }

        case REMOVE_ACTION: {
            return (
                <td className="td" key={REMOVE_ACTION}>
                    <div className="tableCell">
                        <IconTextButton
                            icon={Delete20Regular}
                            text={""}
                            id={`${field.data}`}
                            onClick={() => onClickRemove(tableContentItem)}
                            disabled={
                                isReadOnly ||
                                (stateKey === DISTRIBUTION_POOLS_KEY &&
                                    tableContentItem?.status?.value === "Allocated")
                            }
                        />
                    </div>
                </td>
            );
        }

        case SEARCH_ACTION: {
            return (
                <td className="td" key={SEARCH_ACTION}>
                    <div className="tableCell">
                        <IconTextButton
                            icon={Delete20Regular}
                            text={field.data}
                            onClick={() => showSearchModal(tableContentItem, componentInstance)}
                            disabled={isReadOnly}
                        />
                    </div>
                </td>
            );
        }

        case DEPENDENT_ROW_ACTION: {
            let disabled = isReadOnly;

            if (componentInstance === CONTRIBUTORS_KEY) {
                disabled = tabs[activeTab]?.isReadonly;
            }

            return (
                <td className="td" key={DEPENDENT_ROW_ACTION}>
                    <div className="tableCell">
                        <IconTextButton
                            icon={Open20Regular}
                            text={field.data}
                            onClick={(event) => dependentRowAction(tableContentItem, event)}
                            disabled={disabled}
                        />
                    </div>
                </td>
            );
        }

        case CODE_ACTION: {
            const key = CODE_ACTION
                .concat("_")
                .concat(tableContentItem.workSubmissionID)
                .concat(field.name === WORK_SUBMISSION ? WORK_SUBMISSION : WORK_RESPONSE);

            const code =
                field.name === WORK_SUBMISSION
                    ? tableContentItem.submission
                    : tableContentItem.response;

            return (
                <td className="td" key={key}>
                    <div className="tableCell hasCode">
                        <code>{code}</code>
                        <IconTextButton
                            icon={ArrowMaximize20Regular}
                            text=""
                            onClick={() =>
                                onOpenCodeModel(
                                    field.name === WORK_SUBMISSION
                                        ? tableContentItem.submission
                                        : tableContentItem.response,
                                    field.data
                                )
                            }
                            disabled={isReadOnly}
                        />
                    </div>
                </td>
            );
        }

        case GROUPED_ACTION: {
            return (
                <td className="td" key={field.name}>
                    <div className="tableCell">
                        {renderGroupedActions({
                            tableContentItem,
                            field,
                            groupActionClick,
                        })}
                    </div>
                </td>
            );
        }

        case MERGE_ACTION: {
            const disabled = !tableContentItem.repertoireAgreementID.value;

            return (
                <td className="td" key={field.name}>
                    <div className="tableCell">
                        <button
                            disabled={disabled}
                            type="button"
                            key={field.name}
                            title="Merge"
                            onClick={() => mergeActionClick?.(tableContentItem)}
                            className="button btn-defaultSecondary mergeButton"
                        >
                            Merge
                        </button>
                    </div>
                </td>
            );
        }

        default: {
            return null;
        }
    }
};